// Canonical SEC form descriptions (UI fallback) based on Wikipedia's "All filing types" table:
// https://en.wikipedia.org/wiki/SEC_filing
//
// Notes:
// - We normalize common variants (e.g. `/A` amendments, `DEF14A` vs `DEF 14A`, `SCHEDULE 13D` vs `SC 13D`).
// - Some rare EDGAR codes appear without a description in the table snapshot; we still provide a reasonable label.

const FORM_TYPE_DESCRIPTION_MAP = Object.freeze({
  // Common filing types
  '8-K': 'Events or changes between quarterly reports.',
  '6-K': 'Current report of foreign issuer.',
  '10-K': 'Annual report pursuant to section 13 and 15(d).',
  '10-Q': 'Quarterly report pursuant to section 13 and 15(d).',
  '20-F':
    'Annual and transition report of foreign private issuers pursuant to sections 13 or 15(d).',
  '40-F':
    'Annual report pursuant to section 13(a) or 15(d) of the Securities Exchange Act of 1934 for Canadian issuers.',

  // Proxy statements (common variants)
  'DEF 14A': 'Definitive proxy statement.',
  DEFA14A: 'Additional definitive proxy soliciting materials and/or information statement.',
  DEFC14A: 'Definitive proxy statement in connection with contested solicitations.',
  DEFM14A: 'Definitive proxy statement relating to merger or acquisition.',
  DEFR14A: 'Definitive revised proxy statement.',
  DFAN14A: 'Definitive additional materials filed by non-management.',
  'PRE 14A': 'Preliminary proxy statement.',
  PREC14A: 'Preliminary proxy statement in connection with contested solicitations.',
  PREM14A: 'Preliminary proxy statement relating to merger or acquisition.',
  PRER14A: 'Preliminary revised proxy statement.',

  // Insider forms
  'FORM 3': 'Initial statement of beneficial ownership.',
  '3': 'Initial statement of beneficial ownership.',
  'FORM 4': 'Statement of changes in beneficial ownership.',
  '4': 'Statement of changes in beneficial ownership.',
  'FORM 5': 'Annual statement of changes in beneficial ownership.',
  '5': 'Annual statement of changes in beneficial ownership.',

  // Beneficial ownership schedules
  'SC 13D':
    'Schedule filed to report acquisition of beneficial ownership of more than 5% of a class of equity securities.',
  'SC 13D/A': 'Amendment to Schedule 13D.',
  'SC 13G':
    'Schedule filed to report acquisition of beneficial ownership of more than 5% of a class of equity securities by passive investors and certain institutions.',
  'SC 13G/A': 'Amendment to Schedule 13G.',

  // Additional forms requested
  '144': 'Filing for proposed sale of securities under Rule 144.',
  '25-NSE':
    'Notification filed by national securities exchange to report the removal from listing and registration of matured, redeemed or retired securities.',
  '424B3': 'Prospectus.',
  '425':
    'Filing under Securities Act of 1933 Rule 425 of certain prospectuses and communications in connection with business combination transactions.',
  '8-A12B': 'Registration of a class of securities on a national securities exchange.',
  ARS: 'Annual report to security holders.',
  CERT: 'Certificate.',
  CORRESP: 'Correspondence.',
  EFFECT: 'Notice of effectiveness.',
  FWP: 'Filing under Securities Act Rule 163/433 of free writing prospectus.',
  'NT 10-K':
    'Notice under Rule 12b-25 of inability to timely file all or part of a Form 10-K, 10-KSB, or 10-KT.',
  'NT 10-Q':
    'Notice under Rule 12b-25 of inability to timely file all or part of a Form 10-Q or 10-QSB.',
  'POS AM':
    'Post-effective amendment to a registration statement that is not immediately effective upon filing.',
  'POS EX':
    'Post-effective amendment filed solely to add exhibits to a registration statement.',
  'S-8':
    'Initial registration statement for securities to be offered to employees pursuant to employee benefit plan.',
  'S-3': 'Registration statement for specified transactions by certain issuers.',
  'S-1': 'Registration statement for an initial public offering or other securities offering.',
  '424B4': 'Final prospectus containing offering and pricing details.',
  DRS: 'Draft registration statement submitted for SEC review.',
  'DRS/A': 'Amendment to a draft registration statement.',
  UPLOAD: 'Upload.',

  // Some extra helpful fallbacks (closely-related forms)
  '424A': 'Prospectus.',
  '424B1': 'Prospectus.',
  '424B2': 'Prospectus.',
  '424B5': 'Prospectus.',
  '424B6': 'Prospectus.',
  '424B7': 'Prospectus.',
  '424B8': 'Prospectus.',
});

const normaliseKey = (value) => String(value || '').trim().toUpperCase();

const canonicaliseFormTypeKeyForDescription = (formTypeKey) => {
  const raw = normaliseKey(formTypeKey);
  if (!raw) return '';

  // Preserve exact keys when we explicitly have them (including /A schedule variants).
  if (FORM_TYPE_DESCRIPTION_MAP[raw]) return raw;

  // Normalize common amendment suffixes so "10-K/A" falls back to "10-K".
  const withoutAmendment = raw.replace(/\/A$/, '');

  // Normalize proxy spacing ("DEF14A" -> "DEF 14A", "PRE14A" -> "PRE 14A")
  const spacedProxy = withoutAmendment
    .replace(/^DEF14A$/, 'DEF 14A')
    .replace(/^PRE14A$/, 'PRE 14A');

  // Normalize "SCHEDULE 13D" style names to the abbreviated codes used in this app.
  const scheduleNormalised = spacedProxy
    .replace(/^SCHEDULE\s+13D\/A$/, 'SC 13D/A')
    .replace(/^SCHEDULE\s+13D$/, 'SC 13D')
    .replace(/^SCHEDULE\s+13G\/A$/, 'SC 13G/A')
    .replace(/^SCHEDULE\s+13G$/, 'SC 13G');

  return scheduleNormalised;
};

export const getCanonicalFormDescription = (formTypeKey) => {
  const key = canonicaliseFormTypeKeyForDescription(formTypeKey);
  if (!key) return '';

  // Direct hit
  if (FORM_TYPE_DESCRIPTION_MAP[key]) return FORM_TYPE_DESCRIPTION_MAP[key];

  // Try base form without amendment suffix
  const base = key.replace(/\/A$/, '');
  if (FORM_TYPE_DESCRIPTION_MAP[base]) return FORM_TYPE_DESCRIPTION_MAP[base];

  // Try again with spacing normalization for proxy forms (DEF14A -> DEF 14A)
  const proxySpaced = base.replace(/^DEF14A$/, 'DEF 14A').replace(/^PRE14A$/, 'PRE 14A');
  return FORM_TYPE_DESCRIPTION_MAP[proxySpaced] || '';
};
